// Section 5 — "Fit each model-account" (explains-page-contract.json
// s4_fit): E3-gated soft-cap outcome, cash targets, a per-account
// worked example (model wants → after cap → final target), the
// per-account "Model Review" asset-class view, the cross-account mix
// deviation summary, and the household cost roll-up.
import { Card, Table } from "react-bootstrap";
import { formatCurrency, formatPercent } from "../../../../lib/numbers";
import type {
  AccountRecord,
  CompletionAccountDetail,
} from "../../lib/explainTypes";
import type { SoftSectionContext } from "./SoftCompletionContext";
import {
  CHART_COLORS,
  ChartLegend,
  GroupedColumnChart,
  HBarChart,
} from "./charts";
import {
  CASH_SYMBOL,
  UNKNOWN_AC,
  formatPp,
  groupByAssetClass,
  hasSoftCapEnrichment,
  mixDeviationPp,
  num,
  ownModelAccountIds,
  residualAccountIds,
} from "./softCompletionLib";

// One own-model account with the payload slices section 5 renders.
interface FitAccount {
  accountId: number;
  account: AccountRecord | undefined;
  detail: CompletionAccountDetail | undefined;
  order: number;
}

function fitAccounts(ctx: SoftSectionContext): FitAccount[] {
  return ownModelAccountIds(ctx.completion).map((accountId, i) => ({
    accountId,
    account: ctx.accountsById.get(accountId),
    detail: ctx.completion.account_details?.[String(accountId)],
    order: i,
  }));
}

// E3-gated: how much of each model survived the household-budget cap.
function CapOutcomeChart({
  ctx,
  rows,
}: {
  ctx: SoftSectionContext;
  rows: FitAccount[];
}) {
  if (!hasSoftCapEnrichment(ctx.completion)) return null;
  const withCap = rows.filter((r) => r.detail?.capped_to_pct != null);
  if (withCap.length === 0) return null;
  return (
    <>
      <HBarChart
        title="How much of each model survived the cap"
        rows={withCap.map((r) => {
          const capped = num(r.detail?.capped_to_pct) * 100;
          const leftover = num(r.detail?.leftover_pct) * 100;
          return {
            label: ctx.resolveAccountName(r.accountId),
            sub: `#${r.order + 1} in order`,
            segments: [
              { value: capped, color: CHART_COLORS.accent },
              { value: leftover, color: CHART_COLORS.over },
            ],
            note: `capped to ${formatPercent(num(r.detail?.capped_to_pct), 1)}`,
          };
        })}
        max={100}
      />
      <ChartLegend
        items={[
          {
            label: "capped-to (kept from household budget)",
            color: CHART_COLORS.accent,
          },
          {
            label: "leftover → redeployed across own model",
            color: CHART_COLORS.over,
          },
        ]}
      />
    </>
  );
}

function CashTargetsTable({
  ctx,
  rows,
}: {
  ctx: SoftSectionContext;
  rows: FitAccount[];
}) {
  const withCash = rows.filter((r) => r.account?.cash_adjustment != null);
  if (withCash.length === 0) return null;
  return (
    <Table size="sm" responsive className="mt-3 mb-0">
      <caption className="text-muted small">
        Cash lands on target (cash_adjustment · checkpoints · final_target)
      </caption>
      <thead>
        <tr>
          <th>Account</th>
          <th>Cash setting</th>
          <th className="text-end">Start cash</th>
          <th className="text-end">Final cash</th>
        </tr>
      </thead>
      <tbody>
        {withCash.map((r) => {
          const account = r.account;
          if (account == null) return null;
          const balance = num(account.balance);
          const target = account.cash_adjustment?.target;
          const setting =
            target?.dollar != null
              ? `${formatCurrency(num(target.dollar))} (absolute)`
              : target?.weight != null
                ? `${formatPercent(num(target.weight), 2)} (percent)`
                : "—";
          const startCash = num(account.cash_balance);
          const finalWeight = num(
            account.constrained_allocation?.final_target?.[CASH_SYMBOL],
          );
          return (
            <tr key={r.accountId}>
              <td>{ctx.resolveAccountName(r.accountId)}</td>
              <td>{setting}</td>
              <td className="text-end">
                {formatCurrency(startCash)}
                {balance > 0 && (
                  <span className="text-muted">
                    {" "}
                    ({formatPercent(startCash / balance, 2)})
                  </span>
                )}
              </td>
              <td className="text-end">
                {formatCurrency(finalWeight * balance)}
                <span className="text-muted">
                  {" "}
                  ({formatPercent(finalWeight, 2)})
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}

// Worked example for one own-model account: per-symbol model wants →
// after cap (E3) → final target, as grouped columns + table.
function WorkedExample({
  ctx,
  row,
}: {
  ctx: SoftSectionContext;
  row: FitAccount;
}) {
  const wants = row.account?.model?.primary ?? {};
  const afterCap = row.detail?.after_cap_model;
  const finalTarget = row.account?.constrained_allocation?.final_target ?? {};
  const symbols = [
    ...new Set([
      ...Object.keys(wants),
      ...Object.keys(afterCap ?? {}),
      ...Object.keys(finalTarget).filter((s) => s !== CASH_SYMBOL),
    ]),
  ].sort((a, b) => num(wants[b]) - num(wants[a]));
  if (symbols.length === 0) return null;

  const heading = `${ctx.resolveAccountName(row.accountId)} - ${row.accountId}`;
  return (
    <div className="mt-4">
      <h6>{heading}</h6>
      <GroupedColumnChart
        title="Per-symbol weight: model wants → after cap → final target (% of account)"
        groups={symbols.map((symbol) => ({
          label: symbol,
          columns: [
            {
              value: num(wants[symbol]) * 100,
              color: CHART_COLORS.pale,
              title: `${symbol} model wants`,
            },
            ...(afterCap != null
              ? [
                  {
                    value: num(afterCap[symbol]) * 100,
                    color: CHART_COLORS.over,
                    title: `${symbol} after cap`,
                  },
                ]
              : []),
            {
              value: num(finalTarget[symbol]) * 100,
              color: CHART_COLORS.accent,
              title: `${symbol} final target`,
            },
          ],
        }))}
      />
      <ChartLegend
        items={[
          { label: "model wants", color: CHART_COLORS.pale },
          ...(afterCap != null
            ? [{ label: "after cap", color: CHART_COLORS.over }]
            : []),
          { label: "final target", color: CHART_COLORS.accent },
        ]}
      />
      <Table size="sm" responsive className="mt-2 mb-0">
        <thead>
          <tr>
            <th>Symbol</th>
            <th className="text-end">Model wants</th>
            {afterCap != null && <th className="text-end">After cap</th>}
            <th className="text-end">Final target</th>
          </tr>
        </thead>
        <tbody>
          {symbols.map((symbol) => (
            <tr key={symbol}>
              <td>{symbol}</td>
              <td className="text-end">
                {formatPercent(num(wants[symbol]), 1)}
              </td>
              {afterCap != null && (
                <td className="text-end">
                  {formatPercent(num(afterCap[symbol]), 1)}
                </td>
              )}
              <td className="text-end">
                {formatPercent(num(finalTarget[symbol]), 1)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <ModelReview ctx={ctx} row={row} />
    </div>
  );
}

// Asset-class mixes for one own-model account (contract two_yardsticks).
function accountMixes(ctx: SoftSectionContext, row: FitAccount) {
  const current = groupByAssetClass(
    row.account?.checkpoints?.holdings?.by_symbol,
    ctx.symbolToAc,
  );
  const afterFit = groupByAssetClass(
    row.detail?.constrained_model ?? undefined,
    ctx.symbolToAc,
  );
  const ownModel = groupByAssetClass(
    row.account?.model?.primary,
    ctx.symbolToAc,
  );
  const household = groupByAssetClass(
    ctx.householdModel?.primary,
    ctx.symbolToAc,
  );
  return { current, afterFit, ownModel, household };
}

// "Model Review": current → after fit → own model → household, by
// asset class. `after fit` is the persisted post-stage-4 snapshot
// (account_details.constrained_model), current from checkpoints.
function ModelReview({
  ctx,
  row,
}: {
  ctx: SoftSectionContext;
  row: FitAccount;
}) {
  if (row.detail?.constrained_model == null) return null;
  const { current, afterFit, ownModel, household } = accountMixes(ctx, row);
  const acs = [
    ...new Set([
      ...Object.keys(current),
      ...Object.keys(afterFit),
      ...Object.keys(ownModel),
      ...Object.keys(household),
    ]),
  ]
    .filter((ac) => ac !== UNKNOWN_AC)
    .sort((a, b) => (household[b] ?? 0) - (household[a] ?? 0));
  if (acs.length === 0) return null;
  return (
    <div className="mt-3">
      <h6>Model Review</h6>
      <Table size="sm" responsive className="mb-0">
        <caption className="text-muted small">
          Asset-class view: current → after fit → both targets
          (checkpoints.holdings · constrained_model · model.primary · household
          model)
        </caption>
        <thead>
          <tr>
            <th>Asset class</th>
            <th className="text-end">Current</th>
            <th className="text-end">After fit</th>
            <th className="text-end">Own model</th>
            <th className="text-end">Household</th>
          </tr>
        </thead>
        <tbody>
          {acs.map((ac) => (
            <tr key={ac}>
              <td>{ac}</td>
              <td className="text-end">{formatPercent(current[ac] ?? 0, 1)}</td>
              <td className="text-end">
                {formatPercent(afterFit[ac] ?? 0, 1)}
              </td>
              <td className="text-end">
                {formatPercent(ownModel[ac] ?? 0, 1)}
              </td>
              <td className="text-end">
                {formatPercent(household[ac] ?? 0, 1)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

// Cross-account mix-deviation summary: Σ|deviation| current → after
// fit, against the own model and against the household model.
function MixDeviationSummary({
  ctx,
  rows,
}: {
  ctx: SoftSectionContext;
  rows: FitAccount[];
}) {
  const withModel = rows.filter((r) => r.detail?.constrained_model != null);
  if (withModel.length === 0) return null;
  return (
    <div className="mt-4">
      <h6>Own-model accounts — mix deviation</h6>
      <Table size="sm" responsive className="mb-0">
        <caption className="text-muted small">
          Σ|deviation| by asset class, current → after fit
        </caption>
        <thead>
          <tr>
            <th>Account</th>
            <th className="text-end">Model</th>
            <th className="text-end">→ own model</th>
            <th className="text-end">→ household</th>
          </tr>
        </thead>
        <tbody>
          {withModel.map((r) => {
            const { current, afterFit, ownModel, household } = accountMixes(
              ctx,
              r,
            );
            return (
              <tr key={r.accountId}>
                <td>{ctx.resolveAccountName(r.accountId)}</td>
                <td className="text-end">{r.account?.model?.id ?? "—"}</td>
                <td className="text-end">
                  {formatPp(mixDeviationPp(current, ownModel))} →{" "}
                  <strong>
                    {formatPp(mixDeviationPp(afterFit, ownModel))}
                  </strong>
                </td>
                <td className="text-end">
                  {formatPp(mixDeviationPp(current, household))} →{" "}
                  <strong>
                    {formatPp(mixDeviationPp(afterFit, household))}
                  </strong>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}

// Household cost roll-up: household AC mix current → post-fit → target,
// with the Σ|deviation| total row. Post-fit = own-model accounts at
// constrained_model × balance + no-model accounts still at held mix.
function HouseholdCost({ ctx }: { ctx: SoftSectionContext }) {
  const { completion, accountsById, symbolToAc } = ctx;
  const aum = num(completion.household_aum);
  if (aum <= 0) return null;

  const currentDollars: Record<string, number> = {};
  const postDollars: Record<string, number> = {};
  const addTo = (
    sink: Record<string, number>,
    mix: Record<string, number>,
    balance: number,
  ) => {
    for (const [ac, w] of Object.entries(mix)) {
      sink[ac] = (sink[ac] ?? 0) + w * balance;
    }
  };
  const ownIds = new Set(ownModelAccountIds(completion));
  let havePost = false;
  for (const c of completion.account_classifications ?? []) {
    const account = accountsById.get(c.account_id);
    const balance = num(c.balance);
    const held = groupByAssetClass(
      account?.checkpoints?.holdings?.by_symbol,
      symbolToAc,
    );
    addTo(currentDollars, held, balance);
    const constrained =
      completion.account_details?.[String(c.account_id)]?.constrained_model;
    if (ownIds.has(c.account_id) && constrained != null) {
      havePost = true;
      addTo(postDollars, groupByAssetClass(constrained, symbolToAc), balance);
    } else {
      addTo(postDollars, held, balance);
    }
  }
  if (!havePost) return null;

  const target: Record<string, number> = {};
  for (const [ac, dollars] of Object.entries(
    completion.household_target_by_asset_class ?? {},
  )) {
    target[ac] = num(dollars) / aum;
  }
  const acs = [
    ...new Set([
      ...Object.keys(currentDollars),
      ...Object.keys(postDollars),
      ...Object.keys(target),
    ]),
  ]
    .filter((ac) => ac !== UNKNOWN_AC)
    .sort((a, b) => (target[b] ?? 0) - (target[a] ?? 0));
  if (acs.length === 0) return null;

  const currentMix = Object.fromEntries(
    Object.entries(currentDollars).map(([ac, d]) => [ac, d / aum]),
  );
  const postMix = Object.fromEntries(
    Object.entries(postDollars).map(([ac, d]) => [ac, d / aum]),
  );

  return (
    <div className="mt-4">
      <h6>Household cost</h6>
      <Table size="sm" responsive className="mb-0">
        <caption className="text-muted small">
          Household asset-class mix: current → after fit → target (Σ
          constrained_model × balance + held no-model accounts)
        </caption>
        <thead>
          <tr>
            <th>Asset class</th>
            <th className="text-end">Current</th>
            <th className="text-end">After fit</th>
            <th className="text-end">Target</th>
          </tr>
        </thead>
        <tbody>
          {acs.map((ac) => (
            <tr key={ac}>
              <td>{ac}</td>
              <td className="text-end">
                {formatPercent(currentMix[ac] ?? 0, 1)}
              </td>
              <td className="text-end">{formatPercent(postMix[ac] ?? 0, 1)}</td>
              <td className="text-end">{formatPercent(target[ac] ?? 0, 1)}</td>
            </tr>
          ))}
          <tr className="fw-bold">
            <td>Σ |deviation| vs target</td>
            <td className="text-end">
              {formatPp(mixDeviationPp(currentMix, target))}
            </td>
            <td className="text-end">
              {formatPp(mixDeviationPp(postMix, target))}
            </td>
            <td className="text-end">—</td>
          </tr>
        </tbody>
      </Table>
    </div>
  );
}

export default function FitModelAccountsSection({
  ctx,
}: {
  ctx: SoftSectionContext;
}) {
  const rows = fitAccounts(ctx);
  const hasResiduals = residualAccountIds(ctx.completion).length > 0;
  if (rows.length === 0) return null;
  return (
    <Card className="mb-3">
      <Card.Header>Fit each model-account</Card.Header>
      <Card.Body>
        <CapOutcomeChart ctx={ctx} rows={rows} />
        <CashTargetsTable ctx={ctx} rows={rows} />
        {rows.map((row) => (
          <WorkedExample key={row.accountId} ctx={ctx} row={row} />
        ))}
        <MixDeviationSummary ctx={ctx} rows={rows} />
        {hasResiduals && <HouseholdCost ctx={ctx} />}
      </Card.Body>
    </Card>
  );
}
